import { useCentralStore } from "../../store/Central";
import { DataGrid, GridColDef, GridRowsProp } from "@mui/x-data-grid";
import { deDE } from "@mui/x-data-grid/locales";
import { PanelRightOpen, SquareArrowOutUpRight } from "lucide-react";
import { IconButton } from "@mui/material";
import { JobWithShare } from "@/lib/supabase/supabaseTypes";
import { usePatientStore } from "../../store/Patient";
import { PinJob } from "../../pages/job-page/pin-job.component";
import { TooltipWrapper } from "../ui/tooltip-wrapper";
import { ExtendedJobDocument } from "../../lib/utils/calculate";
import { jobDocumentTypeToTagText } from "../../pages/job-page/job-document/job-document.types";
import { useMemo } from "react";
import { formatCurrency } from "../../lib/utils/utils-functions";
import {
	useSheetStore,
	SheetContentType,
} from "../../store/Sheets/sheet.store";
import { useJobStore } from "../../store/Jobs";
import { useNavigate } from "react-router-dom";

interface JobsTableProps {
	jobs?: JobWithShare[];
	documents?: ExtendedJobDocument[];
}

export const JobsTable: React.FC<JobsTableProps> = ({ jobs, documents }) => {
	const { clientId } = useCentralStore((state) => ({
		clientId: state.clientId,
	}));
	const { patientName, patientsLookup } = usePatientStore((state) => ({
		patientName: state.patientName,
		patientsLookup: state.patientsLookup,
	}));
	const openSheet = useSheetStore((state) => state.openSheet);
	const { shareFile, unshareFile, acceptJobRequest, denyJobRequest } =
		useJobStore((state) => ({
			shareFile: state.shareFile,
			unshareFile: state.unshareFile,
			acceptJobRequest: state.acceptJobRequest,
			denyJobRequest: state.denyJobRequest,
		}));
	const navigate = useNavigate();

	const handleOpenJobSheet = (job: Partial<JobWithShare>) => {
		openSheet(SheetContentType.JOB, {
			job,
			onShareFile: shareFile,
			onUnshareFile: unshareFile,
			onAcceptJobRequest: acceptJobRequest,
			onDenyJobRequest: denyJobRequest,
		});
	};

	const rows: GridRowsProp = useMemo(() => {
		if (documents) {
			return documents.map((doc) => ({
				...doc,
				id: doc.job_document_id,
			}));
		}
		return jobs || [];
	}, [documents, jobs]);

	const columns: GridColDef[] = useMemo(() => {
		if (documents) {
			return [
				{
					field: "actions",
					headerName: "",
					width: 110,
					sortable: false,
					disableColumnMenu: true,
					renderCell: (params) => (
						<div className="flex flex-row items-center">
							<TooltipWrapper title="Vorschau">
								<IconButton
									size="small"
									onClick={(e) => {
										e.stopPropagation();
										handleOpenJobSheet({
											id: params.row.job_document_job_id,
											code: params.row.job_code,
											title: params.row.job_title,
											client_id: params.row.job_client_id,
											status: params.row
												.job_document_status,
											patient_id: params.row.patient_id,
											tax_rate: params.row.job_tax_rate,
										});
									}}
									aria-label="Auftragsvorschau öffnen"
								>
									<PanelRightOpen className="w-4 h-4" />
								</IconButton>
							</TooltipWrapper>
							<TooltipWrapper title="Zum Auftrag">
								<IconButton
									size="small"
									onClick={(e) => {
										e.stopPropagation();
										navigate(
											`/client/${params.row.job_client_id}/job/${params.row.job_document_job_id}`
										);
									}}
									aria-label="Zum Auftrag"
								>
									<SquareArrowOutUpRight className="w-4 h-4" />
								</IconButton>
							</TooltipWrapper>
							<PinJob
								job={
									{
										id: params.row.job_document_job_id,
										code: params.row.job_code,
										client_id: params.row.job_client_id,
									} as JobWithShare
								}
							/>
						</div>
					),
				},
				{ field: "job_code", headerName: "Auftrag", width: 130 },
				{
					field: "job_title",
					headerName: "Titel",
					flex: 1,
					minWidth: 180,
					maxWidth: 600,
				},
				{
					field: "job_document_type",
					headerName: "Typ",
					width: 70,
					valueGetter: (params) =>
						jobDocumentTypeToTagText[
							params.value as keyof typeof jobDocumentTypeToTagText
						] || params.value,
				},
				{
					field: "job_document_date",
					headerName: "Datum",
					width: 100,
					sortable: true,
					type: "date",
					valueGetter: (params) =>
						params.value ? new Date(params.value) : null,
					renderCell: (params) =>
						params.value
							? new Date(params.value).toLocaleDateString(
									"de-CH",
									{
										year: "numeric",
										month: "2-digit",
										day: "2-digit",
									}
								)
							: "",
				},
				{
					field: "patient_name",
					headerName: "Patient",
					width: 200,
					valueGetter: (params) => {
						if (
							!params.row.patient_first_name &&
							!params.row.patient_last_name
						)
							return "";
						return patientName({
							patient_id: params.row.patient_id ?? "",
							first_name: params.row.patient_first_name ?? "",
							last_name: params.row.patient_last_name ?? "",
							code: "",
							title: "",
						});
					},
				},
				{
					field: "job_document_amount",
					headerName: "Betrag",
					width: 100,
					type: "number",
					valueGetter: (params) => params.value || 0,
					renderCell: (params) => {
						if (!params.value) return "";
						return formatCurrency(
							params.value,
							params.row.job_currency_text
						);
					},
				},
			];
		}

		return [
			{
				field: "actions",
				headerName: "",
				width: 110,
				sortable: false,
				disableColumnMenu: true,
				renderCell: (params) => (
					<div className="flex flex-row items-center">
						<TooltipWrapper title="Vorschau">
							<IconButton
								size="small"
								onClick={(e) => {
									e.stopPropagation();
									handleOpenJobSheet(params.row);
								}}
								aria-label="Auftragsvorschau öffnen"
							>
								<PanelRightOpen className="w-4 h-4" />
							</IconButton>
						</TooltipWrapper>
						<TooltipWrapper title="Zum Auftrag">
							<IconButton
								size="small"
								onClick={(e) => {
									e.stopPropagation();
									navigate(
										`/client/${clientId}/job/${params.row.id}`
									);
								}}
								aria-label="Zum Auftrag"
							>
								<SquareArrowOutUpRight className="w-4 h-4" />
							</IconButton>
						</TooltipWrapper>
						<PinJob job={params.row} />
					</div>
				),
			},
			{ field: "code", headerName: "Code", width: 130 },
			{
				field: "title",
				headerName: "Titel",
				flex: 1,
				minWidth: 180,
				maxWidth: 600,
			},
			{
				field: "created_at",
				headerName: "Erstellt",
				width: 100,
				sortable: true,
				type: "date",
				valueGetter: (params) =>
					params.value ? new Date(params.value) : null,
				renderCell: (params) =>
					params.value
						? new Date(params.value).toLocaleDateString("de-CH", {
								year: "numeric",
								month: "2-digit",
								day: "2-digit",
							})
						: "",
			},
			{
				field: "patient_name",
				headerName: "Patient",
				width: 200,
				valueGetter: (params) => {
					if (!patientsLookup || !params.row.patient_id) return "";
					return patientName(patientsLookup[params.row.patient_id]);
				},
			},
		];
	}, [documents, jobs, clientId, patientName, patientsLookup, openSheet]);

	return (
		<DataGrid
			sx={{
				height: "100%",
				"& .MuiDataGrid-cell": {
					py: 0,
					cursor: "pointer",
				},
				"& .MuiDataGrid-columnHeaders": {
					minHeight: "36px !important",
					maxHeight: "36px !important",
				},
			}}
			rowHeight={36}
			rows={rows}
			columns={columns}
			localeText={deDE.components.MuiDataGrid.defaultProps.localeText}
			disableRowSelectionOnClick
			disableColumnSelector
			onRowClick={(params) => {
				const jobId = documents
					? params.row.job_document_job_id
					: params.row.id;
				const jobClientId = documents
					? params.row.job_client_id
					: clientId;
				handleOpenJobSheet({
					id: jobId,
					code: documents ? params.row.job_code : params.row.code,
					title: documents ? params.row.job_title : params.row.title,
					client_id: jobClientId,
					status: documents
						? params.row.job_document_status
						: params.row.status,
					patient_id: params.row.patient_id,
				});
			}}
		/>
	);
};
